import { Logger } from '../logger'
import { axiosPublic } from './api'

// Public self-serve sign-up API (no JWT): apply → verify email → choose plan (Stripe Checkout
// with the trial + a payment method) → complete (provisions the center and signs the owner in).
// Every call is unauthenticated and keyed by the opaque registration token from the email link.
// Errors are logged and rethrown so each screen decides how to surface them (like billingApi).

const BASE = '/v1/public/registrations'

const logError = (method, error, params) => {
  Logger.error({ message: error, payload: { file: 'registrationApi', method, params } })
}

/**
 * GET .../config — `{ captcha_provider, captcha_site_key, trial_days, trial_fee_cents, intro_max_per_buyer }`; decides
 * whether the form shows reCAPTCHA, and the intro copy (intro_max_per_buyer = billing.intro.max-per-buyer, CAR-4014).
 */
export const getRegistrationConfig = async () => {
  try {
    const response = await axiosPublic.get(`${BASE}/config`)
    return response?.data
  } catch (error) {
    logError('getRegistrationConfig', error, {})
    throw error
  }
}

/** POST /v1/public/registrations — the sign-up form. Always 202; resolves to `{ registration_id, status, message }`. */
export const submitRegistration = async (form) => {
  try {
    const response = await axiosPublic.post(BASE, form)
    return response?.data
  } catch (error) {
    logError('submitRegistration', error, { owner_email: form?.owner_email })
    throw error
  }
}

/** POST .../verify — consumes the email link. Resolves to the RegistrationView (with a fresh `token`). */
export const verifyRegistration = async (token) => {
  try {
    const response = await axiosPublic.post(`${BASE}/verify`, { token })
    return response?.data
  } catch (error) {
    logError('verifyRegistration', error, {})
    throw error
  }
}

/** GET .../token/{token} — current state of the applicant's own sign-up. */
export const getRegistration = async (token) => {
  try {
    const response = await axiosPublic.get(`${BASE}/token/${encodeURIComponent(token)}`)
    return response?.data
  } catch (error) {
    logError('getRegistration', error, {})
    throw error
  }
}

/** POST .../checkout-session — Stripe Checkout for a tier (trial + card), with an optional promo code. Resolves to `{ url }`. */
export const startRegistrationCheckout = async (token, tier, promoCode) => {
  try {
    const response = await axiosPublic.post(
      `${BASE}/checkout-session`,
      promoCode ? { token, tier, promo_code: promoCode } : { token, tier }
    )
    return response?.data
  } catch (error) {
    logError('startRegistrationCheckout', error, { tier })
    throw error
  }
}

/**
 * POST .../complete — back from Checkout. Provisions the center (idempotent) and resolves to
 * `{ status, calibration_center_id, email, subscription_state, trial_end_date, auth }` where
 * `auth` is the same payload /authenticate returns.
 */
export const completeRegistration = async (token, sessionId) => {
  try {
    const response = await axiosPublic.post(`${BASE}/complete`, { token, session_id: sessionId })
    return response?.data
  } catch (error) {
    logError('completeRegistration', error, {})
    throw error
  }
}

/** POST .../resend-verification — always 200, never says whether the address is known. */
export const resendRegistrationVerification = async (email) => {
  try {
    await axiosPublic.post(`${BASE}/resend-verification`, { email })
    return true
  } catch (error) {
    logError('resendRegistrationVerification', error, {})
    throw error
  }
}

// ----------------------------------------------------------- one-page sign-up (2026-10-22)

/**
 * POST .../email-code — emails a 6-digit code to confirm the login email inside the form. Resolves to
 * `{ message, expires_in_minutes, resend_after_seconds }` — the same answer whether or not the address has an account.
 */
export const sendSignUpEmailCode = async (email, captchaToken) => {
  try {
    const response = await axiosPublic.post(`${BASE}/email-code`, captchaToken ? { email, captcha_token: captchaToken } : { email })
    return response?.data
  } catch (error) {
    logError('sendSignUpEmailCode', error, {})
    throw error
  }
}

/** POST .../email-code/verify — the typed code. Resolves to `{ verified, email_token, message, attempts_left }`. */
export const verifySignUpEmailCode = async (email, code) => {
  try {
    const response = await axiosPublic.post(`${BASE}/email-code/verify`, { email, code })
    return response?.data
  } catch (error) {
    logError('verifySignUpEmailCode', error, {})
    throw error
  }
}

/**
 * POST .../start — the sign-up page's submit: `{ details, tier, promo_code, email_token }`. Resolves to
 * `{ token, checkout_url, auth }`: go to `checkout_url` to pay, or, with no package chosen, `auth` signs the owner in.
 */
export const startSignUp = async ({ details, tier, promoCode, emailToken }) => {
  try {
    const response = await axiosPublic.post(`${BASE}/start`, {
      details,
      tier: tier || null,
      ...(promoCode ? { promo_code: promoCode } : {}),
      email_token: emailToken
    })
    return response?.data
  } catch (error) {
    logError('startSignUp', error, { tier })
    throw error
  }
}

/** POST .../promo-check — a promo code on the sign-up page. Resolves to `{ valid, code, freeDays, tierIds, message }`. */
export const checkSignUpPromoCode = async (code, tier) => {
  try {
    const response = await axiosPublic.post(`${BASE}/promo-check`, { code, tier: tier || null })
    return response?.data
  } catch (error) {
    logError('checkSignUpPromoCode', error, {})
    throw error
  }
}
